// src-mlr-chakra/app.route.dynamic.js

import appconfig from 'app/config';
import appcomponents from 'app/app.components';

routingDynamic.$inject = [
  '$rootScope', '$location', '$urlRouter', '$stateRegistry',
  '$ocLazyLoad', '$state', '$urlMatcherFactory', 'PageService',
  '$anchorScroll', '$timeout' // ADDED
];

export default function routingDynamic(
  $rootScope, $location, $urlRouter, $stateRegistry,
  $ocLazyLoad, $state, $urlMatcherFactory, PageService,
  $anchorScroll, $timeout // ADDED
) {
  const ERROR_STATE = 'error';
  const API_PAGE_CONTENT_PATH = appconfig.production ? appconfig.api + 'chakra/content' : appconfig.apiv3 + 'mlr/content/chakra';
  const EXCLUDED_PATHS = ['/error', '/clear-cache', '/'];
  let lastFetchedPath = null; // Avoid double fetching

  // ADDED: keep a pending hash across transitions
  let pendingHash = null;

  $urlMatcherFactory.caseInsensitive(true);
  $urlMatcherFactory.strictMode(false);

  // Helper: fetch page data from API
  async function fetchPageContent(path) {

    const response = await fetch(API_PAGE_CONTENT_PATH + path);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    if (responseData.status === ERROR_STATE) {
      PageService.resetGlobalPage();
      $state.go(ERROR_STATE, {}, {
        reload: true
      });
      return;
    }
    const page = responseData.data;
    PageService.setGlobalPage(page);
    return page;

  }

  // ADDED: helper to wait until the element exists, then scroll
  function scrollToHashWhenReady(hash, tries = 20, interval = 50) {
    if (!hash) return;
    const id = (hash.charAt(0) === '#') ? hash.slice(1) : hash;
    const el = document.getElementById(id);
    if (el) {
      // Optional: if you have a fixed header, set an offset once (e.g. 72)
      // $anchorScroll.yOffset = 72;
      $location.hash(id);
      // Let $location update in a digest, then perform the scroll
      $timeout(() => $anchorScroll(), 0);
      pendingHash = null;
      return;
    }
    if (tries > 0) {
      $timeout(() => scrollToHashWhenReady(hash, tries - 1, interval), interval);
    } else {
      pendingHash = null;
    }
  }

  // ADDED: when any (ui-view) content finishes loading, try to scroll if we have a pending hash
  const deregisterViewListener = $rootScope.$on('$viewContentLoaded', () => {
    if (pendingHash) {
      // One extra tick to ensure lazy CSS/layout has applied
      $timeout(() => scrollToHashWhenReady(pendingHash), 0);
    }
  });

  // Helper: Handle dynamic routing logic
  function handleDynamicRoute(path, queryParams, hash) { // ADDED param: hash
    fetchPageContent(path)
      .then(page => {
        $rootScope.page = page;
        const component = appcomponents[page.type];
        const stateName = page.type;
        const stateExists = $stateRegistry.get(stateName);

        const state = {
          // Keep your existing URL building exactly as-is
          url: `/${page.slug}?${Object.keys(queryParams).join('&')}`,
          cache: false,
          name: stateName,
          component,
          params: queryParams,
          caseInsensitiveMatch: true,
          lazyLoad: async ($transition$) => {
            const $ocLazyLoad = $transition$.injector().get('$ocLazyLoad');
            try {
              const mod = await import(`./views/${page.type}/${page.type}.view`);
              await $ocLazyLoad.load(mod[component.toUpperCase() + '_VIEW']);
            } catch (err) {
              throw new Error(`Ooops, something went wrong, ${err}`);
            }

          }
        };

        if (!stateExists) {
          $stateRegistry.register(state);
        }

        // ADDED: remember hash before transition; scrolling will happen after view loads
        pendingHash = hash || null;

        // Always transition to the state with query params to ensure they are preserved
        return $state.go(stateName, queryParams, { reload: true });
      })
      .catch(() => {
        PageService.resetGlobalPage();
        $state.go('error', queryParams, { reload: true });
      });
  }

  // Only use $locationChangeStart
  $rootScope.$on('$locationChangeStart', (e) => {

    const path = $location.$$path.toLowerCase();
    const queryParams = $location.search();
    const hash = $location.hash(); // ADDED: capture #fragment (e.g., 'testi')

    // Prevent double fetch on the same navigation event
    if (lastFetchedPath === path && JSON.stringify(queryParams) === JSON.stringify(window.lastQueryParams)) {
      return;
    }

    lastFetchedPath = path;
    window.lastQueryParams = { ...queryParams }; // Track last used query params

    // Always store current query params (useful for error redirection)
    window.locationSearch = queryParams;

    // Only handle dynamic fetch for paths not in excPath
    e.preventDefault(); // (moved up for single call)
    if (!EXCLUDED_PATHS.includes(path)) {
      handleDynamicRoute(path, queryParams, hash); // ADDED: pass hash through
    } else {
      // For excluded paths, just sync the router and preserve query params
      $urlRouter.sync();
      $location.search(queryParams); // Ensure query params stick around

      // ADDED: if a hash exists on excluded paths, attempt deferred scroll too
      pendingHash = hash || null;
      if (pendingHash) {
        // In case there is no state change, still try soon
        $timeout(() => scrollToHashWhenReady(pendingHash), 0);
      }
    }

  });
}
